import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import {
  useDeleteLogement,
  useLogement,
  useUpdateLogement,
} from "@/hooks/useLogements";

/** Couleurs officielles des classes DPE A→G (même spectre que la vue synthèse). */
const DPE_SPECTRUM: Record<string, string> = {
  A: "#34c759", B: "#86d94a", C: "#cdd836", D: "#ffd60a",
  E: "#ff9f0a", F: "#ff6a2c", G: "#ff3b30",
};

/**
 * Détail d'un logement.
 * V1 : affiche les métadonnées + boutons (supprimer, lancer saisie).
 * Le wizard de saisie 2D arrivera en C6.
 */
export default function LogementDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useLogement(id);
  const deleteMutation = useDeleteLogement();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (isLoading) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-6 py-12">Chargement…</div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-6 py-12">
          <p>Logement introuvable.</p>
          <Link className="mt-4 inline-block text-blue-900 underline" to="/dashboard">
            Retour
          </Link>
        </div>
      </Layout>
    );
  }

  const labelChip = data.latest_dpe_label ? (
    <span className="inline-flex items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-gray-500">
        Étiquette DPE estimée
      </span>
      <span
        className="flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-base font-bold text-white"
        style={{ backgroundColor: DPE_SPECTRUM[data.latest_dpe_label] ?? "#8e94a4" }}
      >
        {data.latest_dpe_label}
      </span>
    </span>
  ) : (
    <span className="rounded bg-gray-100 px-2 py-1 text-sm text-gray-600">
      Aucune estimation calculée pour le moment
    </span>
  );

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/dashboard" className="text-sm text-blue-900 underline">
          ← Mes logements
        </Link>
        <h1 className="mt-3 text-2xl font-bold">{data.title}</h1>
        <p className="mt-1 text-sm text-gray-600 capitalize">{data.type}</p>

        <div className="mt-4">{labelChip}</div>

        <section className="mt-8 rounded border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold">Informations</h2>
          <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Detail label="Adresse" value={data.address_line} />
            <Detail label="Code postal" value={data.postal_code} />
            <Detail label="Ville" value={data.city} />
            <Detail label="Année de construction" value={data.construction_year?.toString()} />
            {data.type === "appartement" && (
              <>
                <Detail label="Étage" value={data.floor?.toString()} />
                <Detail label="Chauffage immeuble" value={data.building_heating_type} />
                <Detail label="Ventilation immeuble" value={data.building_ventilation_type} />
                <Detail label="ECS immeuble" value={data.building_ecs_type} />
              </>
            )}
          </dl>
        </section>

        <FiscalSection logementId={id} initialHouseholdSize={data.household_size} initialTaxIncome={data.tax_reference_income_eur} />

        <section className="mt-8 rounded border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold">Saisie & estimation</h2>
          <p className="mt-2 text-sm text-gray-600">
            Saisissez les caractéristiques techniques de votre logement pour obtenir
            une estimation de l&apos;étiquette énergie (méthode 3CL-DPE 2021).
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {data.latest_dpe_label && (
              <Link
                to={`/logements/${id}/dpe?tab=resultat`}
                className="rounded bg-emerald-700 px-4 py-2 font-medium text-white hover:bg-emerald-800"
              >
                Voir mon estimation
              </Link>
            )}
            <Link
              to={`/logements/${id}/dpe`}
              className="rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800"
            >
              {data.latest_dpe_label
                ? "Reprendre la saisie"
                : "Commencer la saisie"}
            </Link>
          </div>
        </section>

        <section className="mt-8 rounded border border-red-200 bg-red-50 p-6">
          <h2 className="text-lg font-semibold text-red-900">Supprimer ce logement</h2>
          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="mt-4 rounded border border-red-700 px-4 py-2 text-red-700 hover:bg-red-100"
            >
              Supprimer
            </button>
          ) : (
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() =>
                  deleteMutation.mutate(id, {
                    onSuccess: () => navigate("/dashboard", { replace: true }),
                  })
                }
                disabled={deleteMutation.isPending}
                className="rounded bg-red-700 px-4 py-2 text-white hover:bg-red-800 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Suppression…" : "Confirmer la suppression"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-100"
              >
                Annuler
              </button>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm">{value || "—"}</dd>
    </div>
  );
}

function FiscalSection({
  logementId,
  initialHouseholdSize,
  initialTaxIncome,
}: {
  logementId: string;
  initialHouseholdSize: number | null;
  initialTaxIncome: number | null;
}) {
  const [householdSize, setHouseholdSize] = useState<string>(
    initialHouseholdSize != null ? String(initialHouseholdSize) : "",
  );
  const [taxIncome, setTaxIncome] = useState<string>(
    initialTaxIncome != null ? String(initialTaxIncome) : "",
  );
  const update = useUpdateLogement(logementId);
  const isFilled = initialHouseholdSize != null && initialTaxIncome != null;

  return (
    <section className="mt-8 rounded border border-gray-200 bg-white p-6">
      <h2 className="text-lg font-semibold">
        Données fiscales du foyer{" "}
        <span className="text-xs font-normal text-gray-500">(optionnel)</span>
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        Ces informations sont utilisées <strong>uniquement pour estimer
        vos aides MaPrimeRénov'</strong> lors des simulations de travaux
        (tier bleu / jaune / violet / rose).{" "}
        <a
          className="text-blue-700 underline"
          href="https://france-renov.gouv.fr/bareme"
          target="_blank"
          rel="noreferrer"
        >
          Barème officiel Anah 2026
        </a>
        . Sans ces données, le tier <em>violet</em> (intermédiaires) est
        appliqué par défaut.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            Nombre de personnes du foyer fiscal
          </span>
          <input
            type="number"
            min={1}
            max={15}
            value={householdSize}
            onChange={(e) => setHouseholdSize(e.target.value)}
            placeholder="ex. 3"
            className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            Revenu fiscal de référence N-1 (€)
          </span>
          <input
            type="number"
            min={0}
            step={1}
            value={taxIncome}
            onChange={(e) => setTaxIncome(e.target.value)}
            placeholder="ex. 42 500"
            className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Indiqué sur votre avis d'imposition (page 1, ligne « Revenu fiscal
            de référence »).
          </span>
        </label>
      </div>

      <button
        type="button"
        onClick={() =>
          update.mutate({
            household_size:
              householdSize === "" ? null : Number(householdSize),
            tax_reference_income_eur:
              taxIncome === "" ? null : Number(taxIncome),
          })
        }
        disabled={update.isPending}
        className="mt-4 rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
      >
        {update.isPending ? "Enregistrement…" : "Enregistrer"}
      </button>
      {update.isSuccess && (
        <p className="mt-3 text-sm text-green-700">
          Données enregistrées. Vos prochaines simulations de travaux pour ce
          logement utiliseront le tier MaPrimeRénov' correspondant.
        </p>
      )}
      {!isFilled && !update.isSuccess && (
        <p className="mt-3 text-xs text-gray-500">
          Vous pourrez modifier ces données à tout moment. Elles restent
          privées et ne sont jamais transmises à des tiers.
        </p>
      )}
    </section>
  );
}
